
import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useObserver } from "mobx-react";
import { storeContext } from '../../context';
import CustomAutoComplete from '../CustomAutoComplete';
import { formatDate } from '../../helpers/date';
import {
  Form,
  Input,
  Switch,
  Row,
  Col,
  Modal,
  Tag,
  Spin,
  Tooltip,
  Button,
  DatePicker,
  Select,
} from 'antd';
import moment from 'moment';
import QRCode from 'qrcode.react';
import LogProduct from './LogProduct';
import RegistryForm from '../RegistryForm';
import ProductPhotos from './ProductPhotos';
import SecurityProduct from './SecurityProduct';
import ProductProduction from './ProductProduction';
import StatisticsProduct from './StatisticsProduct';
import ProductFilters from './ProductFilters';
import ProductNsServer from './ProductNsServer';
import ProductAsServer from './ProductAsServer';
import ProductInstallation from './ProductInstallation';
import DataProduct from './DataProduct';
import AlertList from './alert/Alert';
import ProductApplicInfo from './ProductApplicInfo';
import RawData from './RawData';
import api from '../../helpers/api';
import translate from '../../i18n/translate';
import { Bus } from '../../helpers/utils';
import InstallationLogs from './InstallationLogs';
import InstallationTab from './InstallationTab';
import ProductionTab from './ProductionTab';
import DataTab from './DataTab';
import OtherTab from './OtherTab';

const { Option } = Select;

const PRODUCT_STATE_OPTIONS = [
  { value: 'jig', label: 'JIG' },
  { value: 'finish', label: 'FINISH' },
];

function getProductIdLabelFromSerial(serial) {
  if (!serial) return null;
  const normalized = String(serial).trim().toUpperCase();
  if (normalized.length < 10) return null;
  const idPart = normalized.substring(4, 10);
  return /^\d{6}$/.test(idPart) ? idPart : null;
}

function formatProductStateLabel(state) {
  return state ? String(state).toUpperCase() : '';
}

function formatFirmwareVersionLabel(firmwareTypeName, versionNumber) {
  if (!versionNumber) return '';
  return firmwareTypeName ? `${firmwareTypeName} - ${versionNumber}` : String(versionNumber);
}

const columns = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    sorter: true,
  },
  {
    title: 'Device Type',
    dataIndex: 'device_type_name',
    key: 'device_type_name',
    sorter: true,
  },
  {
    title: 'FW Version',
    dataIndex: 'firmware_version_number',
    key: 'firmware_version_number',
    sorter: true,
  },
  {
    title: 'Serial',
    dataIndex: 'serial',
    key: 'serial',
    sorter: true,
  },
  {
    title: 'Client',
    dataIndex: 'client_name',
    key: 'client_name',
    sorter: true,
  },
  {
    title: 'Serial Radio',
    dataIndex: 'radio_serial',
    key: 'radio_serial',
    sorter: true,
  },
  {
    title: 'Client SN',
    dataIndex: 'client_sn',
    key: 'client_sn',
    sorter: true,
  },
  {
    title: 'Product State',
    dataIndex: 'product_state',
    key: 'product_state',
    sorter: true,
    render: (text) => formatProductStateLabel(text),
  },
];

const labelCol = {
  xs: 24,
  sm: 24,
  md: 12,
  lg: 12,
  xl: 10,
};

const labelColLeft = {
  xs: 24,
  sm: 24,
  md: 19,
  lg: 19,
  xl: 17,
};

const labelColRight = {
  xs: 24,
  sm: 24,
  md: 8,
  lg: 8,
  xl: 6,
};


let ContentForm = () => {
  const store = React.useContext(storeContext);
  const firmwareVersionRef = useRef();
  const [newTag, setNewTag] = useState('');

  function getMessages() {
    api.get(`product/${store.currentItem.id}/messages?only_active=1&on_admin=1`).then(data => {
      if (store.currentItem && data.data.result && data.data.result.length) {
        Modal.info({
          icon: false,
          width: 500,
          title: translate('default.information'),
          content: data.data.result.map((value, index) => <div key={index}><strong>{`${translate('default.message')} #${index + 1}`}</strong>: {value.message}<br /> </div>)
        });
      }
    })
  }

  function getTags() {
    api.get(`/product/${store.currentItem.id}/tags/all`).then(data => {
      if (store.currentItem && data.data.result) {
        store.currentItem.tags = data.data.result;
      }
    }).finally(() => store.currentItem ? store.currentItem.loadingTags = false : null);
  }

  function buildColorTags(tag) {
    if (!tag.active) return null
    return {
      'application': 'blue',
      'user': 'green',
      'system': 'orange',
    }[tag.type] || null;
  }

  function buildDateToolTip(tag) {
    const datesArr = tag.metadata.insert_date;
    if(datesArr.length){
      return datesArr.map(date => <p key={date}>{formatDate(date)}</p>  );
    }
  }

  function tags() {
    const tags = store.currentItem.tags || [];
    if (tags.length) {
      return tags.map(v => <Tooltip title={buildDateToolTip(v)} key={v.id}><Tag color={buildColorTags(v)}>{v.name}</Tag></Tooltip>);
    }
    if (store.currentItem.loadingTags) {
      return <Spin />;
    }
    return translate('default.noTagsRegistered');
  }

  const handleInputChange = (field, event) => {
    store.currentItem[field] = event.target.value
  }

  const handleSerialChange = (event) => {
    store.currentItem.serial = event.target.value.toUpperCase();
  }

  const handleNewTag = (event) => {
    setNewTag(event.target.value);
  }

  const handleSaveTag = (event) => {
    setNewTag('');
    store.currentItem.loadingTags = true;
    api.post(`/product/${store.currentItem.id}/tags`, {name: newTag, type:'user'}).then(data => {
      getTags();
      console.log(data);
    });
  }

  const handleComboSelect = (field, value) => {
    store.currentItem[field] = value
  }

  const handleSelectDeviceType = (field, value, selectedObj) => {
    store.currentItem[field] = value;
    if (selectedObj && selectedObj.firmware_type_id) {
      store.currentItem.firmware_type_id = selectedObj.firmware_type_id;
      store.currentItem.firmware_type_name = selectedObj.firmware_type_name;
      if (!store.currentItem.id) {
        store.currentItem.firmware_version_id = undefined;
      }
    }
  }

  const handleSelectOrder = (field, value, selectedObj) => {
    store.currentItem[field] = value;
    if (!selectedObj) {
      return;
    }
    if (selectedObj.client_id) {
      store.currentItem.client_id = selectedObj.client_id;
    }
    if (selectedObj.device_type_id) {
      store.currentItem.device_type_id = selectedObj.device_type_id;
    }
    if (selectedObj.firmware_type_id) {
      store.currentItem.firmware_type_id = selectedObj.firmware_type_id;
      store.currentItem.firmware_type_name = selectedObj.firmware_type_name;
      store.currentItem.firmware_version_id = undefined;
    }
    if (selectedObj.firmware_version_id) {
      store.currentItem.firmware_version_id = selectedObj.firmware_version_id;
    }
  }

  const handleSelectFirmwareType = (field, value, selectedObj) => {
    store.currentItem.firmware_version_id = undefined;
    store.currentItem[field] = value;
    if (selectedObj && selectedObj.name) {
      store.currentItem.firmware_type_name = selectedObj.name;
    }
  }

  const handleDateManufactureChange = (date) => {
    store.currentItem.date_manufacture = date
      ? date.format('YYYY-MM-DD HH:mm:ss')
      : undefined;
  }

  const handleProductStateChange = (value) => {
    store.currentItem.product_state = value;
  }

  const initializeNewProductDefaults = useCallback(() => {
    if (!store.currentItem || store.currentItem.id) {
      return;
    }
    if (!store.currentItem.product_state) {
      store.currentItem.product_state = 'finish';
    }
    if (!store.currentItem.date_manufacture) {
      store.currentItem.date_manufacture = moment().format('YYYY-MM-DD HH:mm:ss');
    }
    if (store.currentItem.ready_to_install == null) {
      store.currentItem.ready_to_install = 1;
    }
  }, [store]);

  const handleSwitchChange = (field, checked) => {
    store.currentItem[field] = checked ? 1 : 0;
  }

  const initialize = useCallback(() => {
    if (store.currentItem.id) {
      store.currentItem.tags = [];
      store.currentItem.loadingTags = true;
      Promise.all([getMessages(), getTags()]);
    }
    // eslint-disable-next-line
  }, [store.currentItem.id]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    initializeNewProductDefaults();
  }, [initializeNewProductDefaults, store.currentItem]);

  const currentItemId = store.currentItem ? store.currentItem.id : undefined;

  useEffect(() => {
    const item = store.currentItem;
    if (!item || !item.id || item.firmware_type_id) {
      return;
    }

    const resolveFirmwareType = async () => {
      if (!item.firmware_version_id) {
        return;
      }

      try {
        const { data } = await api.get(`/firmware_version/${item.firmware_version_id}`);
        const firmwareVersion = data.result;
        if (!firmwareVersion || !firmwareVersion.firmware_type_id) {
          return;
        }

        item.firmware_type_id = firmwareVersion.firmware_type_id;

        const typeResponse = await api.get(`/firmware_type/${firmwareVersion.firmware_type_id}`);
        if (typeResponse.data.result && typeResponse.data.result.name) {
          item.firmware_type_name = typeResponse.data.result.name;
        }
      } catch (error) {
        console.error('Failed to resolve firmware type for product', error);
      }
    };

    resolveFirmwareType();
  }, [store, currentItemId]);

  return useObserver(() => {
    const isNewProduct = !store.currentItem || !store.currentItem.id;
    const predictedProductId = isNewProduct
      ? getProductIdLabelFromSerial(store.currentItem.serial)
      : null;

    return (
    <Form layout="vertical">
      <Row gutter={16}>
        <Col {...labelCol} >
          <Form.Item label="Id">
            <Input value={store.currentItem.id} disabled={true} />
          </Form.Item>
        </Col>
        <Col {...labelCol} >
          <Form.Item label="Created At">
            <Input value={formatDate(store.currentItem.created_at)} disabled={true} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col {...labelCol} >
          <Form.Item label="Updated At">
            <Input value={formatDate(store.currentItem.updated_at)} disabled={true} />
          </Form.Item>
        </Col>
        <Col {...labelCol}>
          <Form.Item label="Product State">
            {isNewProduct ? (
              <Select
                value={store.currentItem.product_state}
                placeholder="Select the product state"
                onChange={handleProductStateChange}
              >
                {PRODUCT_STATE_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>{option.label}</Option>
                ))}
              </Select>
            ) : (
              <Input value={formatProductStateLabel(store.currentItem.product_state)} disabled={true} />
            )}
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col {...labelCol}>
          <Form.Item label="Client" required={isNewProduct}>
            <CustomAutoComplete
              searchValue={'id'}
              searchKey={'name'}
              path={'/client'}
              placeholder={'Select the Client'}
              onSelect={handleComboSelect.bind(this, 'client_id')}
              value={store.currentItem.client_id} />
          </Form.Item>
        </Col>
        <Col {...labelCol} >
          <Form.Item label="Device Type" required={isNewProduct}>
            <CustomAutoComplete
              searchValue={'id'}
              searchKey={'name'}
              path={'/device_type'}
              placeholder={'Select the Device Type'}
              onSelect={handleSelectDeviceType.bind(this, 'device_type_id')}
              value={store.currentItem.device_type_id} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col {...labelCol}>
          <Form.Item label="Order" required={isNewProduct}>
            <CustomAutoComplete
              searchValue={'id'}
              searchKey={'order_description'}
              path={'/order'}
              placeholder={'Select the order'}
              onSelect={handleSelectOrder.bind(this, 'order_id')}
              value={store.currentItem.order_id}
              disabled={!isNewProduct} />
          </Form.Item>
        </Col>
        <Col {...labelCol}>
          <Form.Item label="Date Manufacture" required={isNewProduct}>
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Select manufacture date"
              value={store.currentItem.date_manufacture ? moment(store.currentItem.date_manufacture) : null}
              onChange={handleDateManufactureChange}
              disabled={!isNewProduct}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col {...labelCol}>
          <Form.Item label="Firmware Type" required={isNewProduct}>
            <CustomAutoComplete
              searchValue={'id'}
              searchKey={'name'}
              path={'/firmware_type'}
              placeholder={'Select the firmware type'}
              onSelect={handleSelectFirmwareType.bind(this, 'firmware_type_id')}
              value={store.currentItem.firmware_type_id} />
          </Form.Item>
        </Col>
        <Col {...labelCol}>
          <Form.Item label="Firmware Version" required={isNewProduct}>
            <CustomAutoComplete
              key={store.currentItem.firmware_type_id || 'no-firmware-type'}
              ref={firmwareVersionRef}
              searchValue={'id'}
              searchKey={'version_number'}
              path={store.currentItem.firmware_type_id
                ? `/firmware_type/${store.currentItem.firmware_type_id}/version`
                : undefined}
              notRequest={!store.currentItem.firmware_type_id}
              placeholder={store.currentItem.firmware_type_id
                ? 'Select the firmware version'
                : 'Select the firmware type first'}
              labelFormatter={(item) => formatFirmwareVersionLabel(
                store.currentItem.firmware_type_name,
                item.version_number,
              )}
              onSelect={handleComboSelect.bind(this, 'firmware_version_id')}
              value={store.currentItem.firmware_version_id}
              disabled={!store.currentItem.firmware_type_id}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col {...labelCol}>
          <Form.Item label="Serial CM" required={isNewProduct}>
            <Input
              value={store.currentItem.serial_cm}
              onChange={handleInputChange.bind(this, 'serial_cm')}
              disabled={!isNewProduct}
              placeholder="CM-000123"
            />
          </Form.Item>
        </Col>
        <Col {...labelCol}>
          <Form.Item
            label="Serial"
            required={isNewProduct}
            extra={predictedProductId ? `ID previsto (regra jig): ${predictedProductId}` : null}
          >
            <Input
              value={store.currentItem.serial}
              onChange={handleSerialChange}
              placeholder="S20E092899LA"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col {...labelColLeft}>
          <Row gutter={16}>
            <Col xs={24} sm={24} md={24} lg={24} xl={14} style={{ marginLeft: 1 }}>
              <Form.Item label="Serial Radio">
                <Input value={store.currentItem.radio_serial} onChange={handleInputChange.bind(this, 'radio_serial')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col>
              <Col xs={24} sm={24} md={24} lg={24} xl={8} style={{ marginLeft: 4 }}>
                <Form.Item label="Ready to Install">
                  <Switch onChange={handleSwitchChange.bind(this, 'ready_to_install')} checked={Boolean(store.currentItem.ready_to_install)} />
                </Form.Item>
              </Col>
            </Col>
          </Row>
          {store.currentItem.tags && <Row gutter={16}>
            <Col xs={24} sm={24} md={24} lg={24} xl={14}>
              <Col>
              <Form.Item label="Tags">
                {tags()}
              </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item >
                  <Input value={newTag} onChange={handleNewTag} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item >
                  <Button onClick={handleSaveTag} loading={store.currentItem.loadingTags}>Save</Button>
                </Form.Item>
              </Col>
            </Col>
          </Row>}
        </Col>
        <Col {...labelColRight}>
          <Col xs={24} sm={24} md={14} lg={14} xl={8}>
            {store.currentItem.serial && <Form.Item label="QRCode">
              <QRCode
                value={store.currentItem.serial}
              />
            </Form.Item>}
          </Col>
        </Col>
      </Row>
    </Form>
    );
  });
}

const ProductList = () => {
  const store = React.useContext(storeContext);

  const refreshCurrentProduct = useCallback(async () => {
    const item = store.currentItem;
    if (!item || !item.id) {
      return;
    }

    try {
      const [{ data: detailData }, { data: listData }] = await Promise.all([
        api.get(`/product/${item.id}`),
        api.get('/product', { params: { filter: String(item.id), limit: 1 } }),
      ]);

      const listItem = (listData.result || []).find(
        (product) => String(product.id) === String(item.id),
      ) || (listData.result || [])[0];

      const merged = {
        ...item,
        ...(detailData.result || {}),
        ...(listItem || {}),
      };

      if (item.firmware_type_id) {
        merged.firmware_type_id = item.firmware_type_id;
      }
      if (item.firmware_type_name) {
        merged.firmware_type_name = item.firmware_type_name;
      }

      store.currentItem = merged;
      store.currentItemBkp = { ...merged };

      if (store.data) {
        const listIndex = store.data.findIndex(
          (product) => String(product.id) === String(item.id),
        );
        if (listIndex >= 0) {
          store.data[listIndex] = { ...merged };
        }
      }

      Bus.emit('reloadCustomTabs');
    } catch (error) {
      console.error('Failed to refresh product after save', error);
    }
  }, [store]);

  useEffect(() => {
    Bus.addListener('productAfterSave', refreshCurrentProduct);
    return () => Bus.removeListener('productAfterSave', refreshCurrentProduct);
  }, [refreshCurrentProduct]);

  ContentForm = Form.create({})(ContentForm);

  store.editForm = undefined;
  InstallationTab.tab = 'Installation';
  ProductionTab.tab = 'Production';
  DataTab.tab = 'Data';
  OtherTab.tab = 'Other';
  
  ContentForm.tab = 'Info';
  ProductPhotos.tab = 'default.photos';
  LogProduct.tab = 'Log';
  SecurityProduct.tab = 'Keys';
  ProductProduction.tab = 'Production';
  StatisticsProduct.tab = 'Statistics';
  ProductNsServer.tab = 'NS Server';
  ProductAsServer.tab = 'AS Server';
  ProductInstallation.tab = 'Installation';
  DataProduct.tab = 'App Data';
  AlertList.tab = 'alert.name';
  ProductApplicInfo.tab = 'Applic Info';
  RawData.tab = 'Raw Data';
  InstallationLogs.tab = 'product.installationLogs';
  
  InstallationTab.disabled = true;
  ProductionTab.disabled = true;
  DataTab.disabled = true;
  OtherTab.disabled = true;

  ProductPhotos.disabled = true;
  LogProduct.disabled = true;
  SecurityProduct.disabled = true;
  ProductProduction.disabled = true;
  StatisticsProduct.disabled = true;
  ProductNsServer.disabled = true;
  ProductAsServer.disabled = true;
  ProductInstallation.disabled = true;
  DataProduct.disabled = true;
  AlertList.disabled = true;
  ProductApplicInfo.disabled = true;
  RawData.disabled = true;
  InstallationLogs.disabled = true;

  ContentForm.hideReload = true;
  InstallationTab.hideReload = true;
  ProductionTab.hideReload = true;
  DataTab.hideReload = true;
  OtherTab.hideReload = true;


  store.tabs = [ContentForm, InstallationTab, ProductionTab, SecurityProduct, DataTab, OtherTab, LogProduct, AlertList]

  store.installationTabs = [ProductInstallation, ProductPhotos, ProductApplicInfo, InstallationLogs]

  store.productionTabs = [ProductProduction, StatisticsProduct]

  store.dataTabs = [DataProduct, RawData]

  store.otherTabs = [ProductNsServer, ProductAsServer]

  store.columns = columns;
  store.customFilters = ProductFilters;
  store.url = '/product';
  store.exportUrl = '/product/csv/export';

  useEffect(() => {
    store.customFilters = ProductFilters;
    return () => { store.customFilters = null; }
  }, [store.customFilters]);

  return (
    <div>
      <RegistryForm eventSave="productAfterSave" />
    </div>
  )
}

export default ProductList;
